/** Search layout mirrors detail card columns (spacers = CMP, Futures, Lots, collapse). */
export const STRATEGY_SEARCH_LAYOUT = [
  { type: "field", key: "scrip", columnType: "text", label: "Scrip", placeholder: "Search Scrip" },
  { type: "spacer", id: "cmp" },
  { type: "spacer", id: "future" },
  { type: "field", key: "ceStrike", columnType: "numeric", label: "CE Strikes", placeholder: "CE e.g. >23500" },
  { type: "field", key: "peStrike", columnType: "numeric", label: "PE Strikes", placeholder: "PE e.g. <23500" },
  { type: "spacer", id: "lots" },
  { type: "field", key: "mpErosion", columnType: "numeric", label: "Max Profit Erosion", placeholder: "Max Profit Erosion" },
  { type: "spacer", id: "collapse" },
];

const CARD_COLORS = [
  { iconColor: "#4F46E5", iconBg: "#EEF2FF" },
  { iconColor: "#10B981", iconBg: "#ECFDF5" },
  { iconColor: "#F97316", iconBg: "#FFF7ED" },
  { iconColor: "#14B8A6", iconBg: "#F0FDFA" },
  { iconColor: "#EC4899", iconBg: "#FDF2F8" },
];

export const buildStrategyCardsViewModel = (apiResponse = {}) => {
  const strategies = apiResponse?.strategy_detail || apiResponse?.data?.strategy_detail || apiResponse || [];

  if (!Array.isArray(strategies)) return [];

  return strategies.map((strategy, index) => ({
    id: strategy.strategy_id,
    name: strategy.strategy_name?.toUpperCase() || "UNKNOWN",
    type: strategy.strategy_type,
    pnl: Number(strategy.strategy_pnl) || 0,
    iconColor: CARD_COLORS[index % CARD_COLORS.length].iconColor,
    iconBg: CARD_COLORS[index % CARD_COLORS.length].iconBg,
  }));
};

const getInfinitySymbol = (value) => {
  if (value === Infinity) return "∞";
  if (value === -Infinity) return "-∞";
  const normalizedValue = String(value).toLowerCase();
  if (normalizedValue === "∞" || normalizedValue === "inf" || normalizedValue === "infinity") return "∞";
  if (normalizedValue === "-∞" || normalizedValue === "-inf" || normalizedValue === "-infinity") return "-∞";
  return null;
};

const isInfiniteValue = (value) => getInfinitySymbol(value) != null;

const toDisplayPercent = (value) => {
  if (value == null || value === "") return "--";
  if (isInfiniteValue(value)) return getInfinitySymbol(value);
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return String(value);
  return Number(numericValue.toFixed(2));
};

const toDisplayNumber = (value) => {
  if (value == null || value === "") return "";
  if (isInfiniteValue(value)) return getInfinitySymbol(value);
  return value;
};

const joinStrikes = (values) => (values?.length ? values.map(toDisplayNumber).join(", ") : "--");

const isSummaryEntry = (value) =>
  value != null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  (Array.isArray(value.group_list) || value.strategy_name != null);

const isNumericObjectKey = (key) => /^\d+$/.test(String(key));

const buildSummaryKeyFromSummary = (summary) => {
  const symbol = summary.symbol || summary.group_list?.[0]?.symbol || "STRATEGY";
  const cePart = (summary.ce_strikes || []).map((strike) => String(strike)).join("_");
  const pePart = (summary.pe_strikes || []).map((strike) => String(strike)).join("_");

  if (cePart && pePart) return `${symbol}_CE_${cePart}_PE_${pePart}`;
  if (pePart) return `${symbol}_PE_${pePart}`;
  if (cePart) return `${symbol}_CE_${cePart}`;
  return summary.group_list?.[0]?.summary_key || symbol;
};

const toSummaryKeyMap = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};

  const entries = Object.entries(value).filter(
    ([key, entry]) => !isNumericObjectKey(key) && isSummaryEntry(entry)
  );

  if (entries.length > 0) {
    return Object.fromEntries(entries);
  }

  if (isSummaryEntry(value)) {
    const summaryKey = value.group_list?.[0]?.summary_key || buildSummaryKeyFromSummary(value);
    return { [summaryKey]: value };
  }

  return {};
};

/** API returns JS-like JSON text (Infinity / -Infinity are not valid JSON). */
const sanitizeReportJsonString = (text) =>
  text
    .replace(/:\s*-Infinity\b/g, ': "-Infinity"')
    .replace(/:\s*Infinity\b/g, ': "Infinity"')
    .replace(/:\s*NaN\b/g, ": null");

const tryParseReportJson = (text) => {
  const trimmed = text.trim();
  if (!trimmed) return null;

  try {
    return JSON.parse(sanitizeReportJsonString(trimmed));
  } catch {
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
};

const parseJsonValue = (value) => {
  if (typeof value !== "string") return value;

  let parsed = tryParseReportJson(value);
  let depth = 0;

  while (typeof parsed === "string" && depth < 3) {
    parsed = tryParseReportJson(parsed);
    depth += 1;
  }

  return parsed ?? {};
};

const hasSummaryEntries = (value) =>
  value != null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.entries(value).some(([key, entry]) => !isNumericObjectKey(key) && isSummaryEntry(entry));

const unwrapReportEnvelope = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;

  if (value.report != null) {
    return value.report;
  }

  if (value.data != null) {
    const nested = value.data;
    if (typeof nested === "string" || isSummaryEntry(nested) || hasSummaryEntries(nested)) {
      return nested;
    }
  }

  return value;
};

/**
 * API returns a JSON string (not a parsed object), e.g.
 * "{\"NIFTY_CE_23950.0_PE_23950.0\": { ... }}"
 * Convert to `{ [summaryKey]: summary }` for the view model.
 */
export const parseStrategyReportResponse = (response) => {
  let raw = response;

  if (typeof raw === "string") {
    raw = parseJsonValue(raw);
  } else if (raw != null && typeof raw === "object" && !Array.isArray(raw)) {
    raw = unwrapReportEnvelope(raw);
    raw = parseJsonValue(raw);
    raw = unwrapReportEnvelope(raw);
    raw = parseJsonValue(raw);
  } else {
    raw = parseJsonValue(raw);
  }

  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }

  return toSummaryKeyMap(raw);
};

const sumPositions = (positions, key) =>
  positions.reduce((total, position) => total + (Number(position[key]) || 0), 0);

const isTrueValue = (value) => value === true || String(value).toLowerCase() === "true";

const getFutureDisplay = (summary) => {
  if (!isTrueValue(summary.has_fut)) return "N/A";
  return isTrueValue(summary.fut_open) ? "true" : "false";
};

const buildPositionRows = (positions, group, groupIndex) =>
  positions.map((position, index) => ({
    id: `${group.summary_key}-${group.broker_client_code}-${group.batch_id || groupIndex}-${position.exchange_token || index}`,
    key: `${group.summary_key}_${group.broker_client_code}_${group.batch_id || groupIndex}_${position.exchange_token || index}`,
    broker_client_code: group.broker_client_code,
    exchange_trading_symbol: position.exchange_trading_symbol,
    exchange: position.exchange,
    exchange_token: position.exchange_token,
    product_type: position.product_type,
    net_quantity: position.net_quantity,
    tran: position.transaction_type === "BUY" ? "B" : "S",
    symbol: position.exchange_trading_symbol,
    strike: position.strike_price,
    sqOff: position.squared_off_quantity,
    buyPrice: position.buy_price,
    sellPrice: position.sell_price,
    netQty: position.net_quantity,
    cmp: position.last_price,
    profit: position.total_profit,
    expiryPnl: position.expiry_pnl ?? position.total_expiry_profit,
    profitPct: toDisplayPercent(position.profit_pct),
    individualLegErosion: toDisplayPercent(position.profit_pct),
    groupIndex,
  }));

const buildClientGroup = (group, groupIndex) => {
  const positions = group.batch_positions || [];
  const rows = buildPositionRows(positions, group, groupIndex);
  const summedExpiryPnl = positions.some((position) => position.expiry_pnl != null)
    ? sumPositions(positions, "expiry_pnl")
    : sumPositions(positions, "total_expiry_profit");

  return {
    clientCode: group.broker_client_code,
    batchId: group.batch_id,
    maxProfitErosion: toDisplayPercent(group.max_profit_erosion),
    maxLossErosion: toDisplayPercent(group.max_loss_erosion),
    individualLegErosion: toDisplayPercent(group.max_loss_erosion),
    maxProfit: toDisplayNumber(group.max_profit),
    maxLoss: toDisplayNumber(group.max_loss),
    breakevenPoints: Array.isArray(group.breakeven_points) ? group.breakeven_points : [],
    breakevenPctAway: Array.isArray(group.breakeven_pct_away)
      ? group.breakeven_pct_away.map(toDisplayPercent)
      : [],
    rows,
    totals: {
      sellPrice: group.net_premium_per_base_quantity,
      netQty: group.base_quantity,
      profit: group.total_profit ?? sumPositions(positions, "total_profit"),
      expiryPnl: group.expiry_pnl ?? group.total_expiry_profit ?? summedExpiryPnl,
      profitPct: toDisplayPercent(group.profit_pct),
      maxProfit: toDisplayNumber(group.max_profit),
      maxProfitErosion: toDisplayPercent(group.max_profit_erosion),
      maxLoss: toDisplayNumber(group.max_loss),
      maxLossErosion: toDisplayPercent(group.max_loss_erosion),
    },
  };
};

export const buildStrategyReportViewModel = (apiResponse = {}) => {
  if (!apiResponse || typeof apiResponse !== "object" || Array.isArray(apiResponse)) {
    return {
      strategyCards: [],
      detailItems: [],
    };
  }

  const detailItems = Object.entries(apiResponse)
    .map(([summaryKey, summary], index) => {
      if (!summary || typeof summary !== "object" || Array.isArray(summary)) return null;

      const groups = summary.group_list || [];
      const firstGroup = groups[0] || {};
      const clientGroups = groups.map(buildClientGroup);
      const positionRows = clientGroups.flatMap((group) => group.rows);
      const totalProfit = clientGroups.reduce((total, group) => total + (Number(group.totals.profit) || 0), 0);

      return {
        id: summaryKey,
        strategyId: summaryKey,
        name: summary.strategy_name,
        batchId: summaryKey,
        scripName: summary.underlying_exchange_trading_symbol || firstGroup.underlying_exchange_trading_symbol || summaryKey,
        cmp: summary.underlying_last_price,
        future: getFutureDisplay(summary),
        ceStrike: joinStrikes(summary.ce_strikes),
        peStrike: joinStrikes(summary.pe_strikes),
        ceStrikes: summary.ce_strikes || [],
        peStrikes: summary.pe_strikes || [],
        ceOpenStrikes: summary.ce_open_strikes || [],
        peOpenStrikes: summary.pe_open_strikes || [],
        lots: summary.lots_open,
        ogErosion: toDisplayPercent(firstGroup.max_loss_erosion),
        maxProfitErosion: toDisplayPercent(summary.max_profit_erosion),
        totalProfit,
        iconColor: CARD_COLORS[index % CARD_COLORS.length].iconColor,
        iconBg: CARD_COLORS[index % CARD_COLORS.length].iconBg,
        clientGroups,
        positionRows,
      };
    })
    .filter(Boolean);

  return {
    strategyCards: detailItems.map((item, index) => ({
      id: item.id,
      name: item.name?.toUpperCase() || item.id,
      iconColor: item.iconColor,
      iconBg: item.iconBg,
      pnl: item.totalProfit,
      active: index === 0,
    })),
    detailItems,
  };
};
